/**
 * Usuwa metadane tekstowe z obrazków przed uploadem.
 *
 * Powód: macOS osadza w zrzutach ekranu metadane XMP — czysty XML
 * (`<x:xmpmeta ...>`) w chunku iTXt PNG / segmencie APP1 JPEG. AWS WAF na
 * prodzie flaguje takie body jako XSS i blokuje request, a CloudFront
 * przepisuje blok na index.html z HTTP 200 — frontend dostaje wtedy
 * "Unexpected token '<' ... is not valid JSON". Strip jest bajtowy i
 * bezstratny dla pikseli (usuwa wyłącznie chunki/segmenty metadanych).
 */

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] as const;
/** Chunki tekstowe PNG — jedyne, które mogą nieść XML/XMP. */
const STRIPPED_PNG_CHUNKS = new Set(["iTXt", "tEXt", "zTXt"]);
/**
 * Segmenty JPEG z metadanymi tekstowymi: APP1 tylko w wariancie XMP (czysty XML —
 * to on triggeruje WAF) i COM (komentarz). VP-230 P3: APP1 z binarnym EXIF-em
 * ZOSTAJE — niesie tag Orientation, bez którego zdjęcia z telefonów lądują
 * obrócone (serwerowa normalizacja nie ma czego naprawiać po wycięciu tagu).
 */
const JPEG_APP1_MARKER = 0xe1;
const JPEG_COM_MARKER = 0xfe;
const EXIF_HEADER = [0x45, 0x78, 0x69, 0x66, 0x00, 0x00] as const; // "Exif\0\0"

/** APP1 zaczynający się od "Exif\0\0" to binarny EXIF — zachowujemy (Orientation). */
function isExifApp1(buf: Uint8Array, segStart: number, segEnd: number): boolean {
  const payloadStart = segStart + 4; // FF E1 <len:2> <payload>
  if (payloadStart + EXIF_HEADER.length > segEnd) return false;
  return EXIF_HEADER.every((b, i) => buf[payloadStart + i] === b);
}

/**
 * Zwraca kopię pliku bez metadanych tekstowych (PNG/JPEG) albo oryginał,
 * gdy nie ma czego usuwać lub format jest nieobsługiwany. Nigdy nie rzuca —
 * przy błędzie parsowania zwraca oryginalny plik.
 */
export async function stripImageMetadata(file: File): Promise<File> {
  try {
    if (file.type === "image/png") return await stripPng(file);
    if (file.type === "image/jpeg") return await stripJpeg(file);
    return file;
  } catch {
    return file;
  }
}

/** Przepuszcza przez strip wszystkie obrazki w FormData (inne wartości bez zmian). */
export async function stripFormDataImages(formData: FormData): Promise<FormData> {
  const entries = Array.from(formData.entries());
  const hasImage = entries.some(
    ([, v]) => v instanceof File && (v.type === "image/png" || v.type === "image/jpeg")
  );
  if (!hasImage) return formData;
  const out = new FormData();
  for (const [key, value] of entries) {
    if (value instanceof File) {
      out.append(key, await stripImageMetadata(value), value.name);
    } else {
      out.append(key, value);
    }
  }
  return out;
}

async function stripPng(file: File): Promise<File> {
  const buf = new Uint8Array(await file.arrayBuffer());
  if (buf.length < 8 || !PNG_SIGNATURE.every((b, i) => buf[i] === b)) return file;

  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const kept: Uint8Array[] = [buf.subarray(0, 8)];
  let changed = false;
  let i = 8;
  while (i + 12 <= buf.length) {
    const length = view.getUint32(i);
    const type = String.fromCharCode(buf[i + 4], buf[i + 5], buf[i + 6], buf[i + 7]);
    const end = i + 12 + length;
    if (end > buf.length) return file; // uszkodzony plik — nie ruszamy
    if (STRIPPED_PNG_CHUNKS.has(type)) {
      changed = true;
    } else {
      kept.push(buf.subarray(i, end));
    }
    i = end;
    if (type === "IEND") break;
  }
  if (!changed) return file;
  return rebuildFile(file, kept);
}

async function stripJpeg(file: File): Promise<File> {
  const buf = new Uint8Array(await file.arrayBuffer());
  if (buf.length < 4 || buf[0] !== 0xff || buf[1] !== 0xd8) return file;

  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const kept: Uint8Array[] = [buf.subarray(0, 2)];
  let changed = false;
  let i = 2;
  while (i + 4 <= buf.length) {
    if (buf[i] !== 0xff) return file; // zdesynchronizowany strumień — nie ruszamy
    const marker = buf[i + 1];
    if (marker === 0xda) {
      // SOS — od tego miejsca dane skanu, kopiujemy resztę w całości
      kept.push(buf.subarray(i));
      break;
    }
    const segLength = view.getUint16(i + 2);
    const end = i + 2 + segLength;
    if (end > buf.length) return file;
    const isTextualMetadata =
      marker === JPEG_COM_MARKER || (marker === JPEG_APP1_MARKER && !isExifApp1(buf, i, end));
    if (isTextualMetadata) {
      changed = true;
    } else {
      kept.push(buf.subarray(i, end));
    }
    i = end;
  }
  if (!changed) return file;
  return rebuildFile(file, kept);
}

function rebuildFile(original: File, parts: Uint8Array[]): File {
  const blob = new Blob(parts as BlobPart[], { type: original.type });
  return new File([blob], original.name, {
    type: original.type,
    lastModified: original.lastModified,
  });
}
